import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../lib/api';

const EmailPreview = () => {
  const { templateId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPreview = async () => {
      try {
        // The client attaches the stored admin credential; the token is never
        // read from the URL (it would leak via history / referrer / logs).
        const response = await adminApi.get(
          `/admin/email-templates/${templateId}/preview`
        );
        setPreview(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load preview');
      } finally {
        setLoading(false);
      }
    };

    fetchPreview();
  }, [templateId, searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FEF7ED' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-business border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FEF7ED' }}>
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-navy/5 text-center max-w-md">
          <div className="w-16 h-16 bg-red-50 rounded-xl flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h1 className="font-display text-xl text-navy mb-2">Preview Error</h1>
          <p className="text-navy/60 mb-6">{error}</p>
          <button
            onClick={() => navigate('/admin/email-templates')}
            className="px-6 py-2 rounded-xl text-white font-medium"
            style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' }}
          >
            Back to Templates
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FEF7ED' }}>
      {/* V5 Header Bar */}
      <div className="bg-white border-b border-navy/5 px-6 py-4 sticky top-0 z-10 shadow-sm">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              type="button"
              aria-label="Back to templates"
              onClick={() => navigate('/admin/email-templates')}
              className="p-2 hover:bg-navy/5 rounded-xl transition-colors"
            >
              <ArrowLeft size={20} className="text-navy/60" aria-hidden="true" />
            </button>
            <div>
              <p className="font-mono text-business text-xs mb-0.5">// email_preview</p>
              <h1 className="font-display text-lg text-navy">
                {preview?.template_id?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </h1>
              <p className="text-sm text-navy/50">Subject: {preview?.subject}</p>
            </div>
          </div>
          <span className="px-3 py-1.5 bg-consumer/10 text-consumer text-xs font-semibold rounded-full">
            Preview Mode
          </span>
        </div>
      </div>

      {/* Email Preview */}
      <div className="py-8 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-navy/5 overflow-hidden">
            <div 
              className="email-preview"
              dangerouslySetInnerHTML={{ __html: preview?.html_content || '' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailPreview;
